import React from 'react';
import { cn } from '~/lib/utils';
import themes from './HeaderTheme.module.css';
import styles from './Avatar.module.css';
import type { HeaderHover, HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface AvatarProps {
  /** Profile picture. Falls back to initials when absent or when the image fails to load. */
  src?: string;
  /** Used for the initials fallback and the accessible name. */
  name?: string;
  /**
   * Overrides the derived fallback. The derivation below takes the first letter of the first two
   * words, which is right for "Guri Sitchinava" and wrong for a one-word name — "Beka" derives `B`
   * where the account itself says `BT`. Callers that already hold the account's own initials (the
   * settings shell does) pass them rather than letting the name be re-guessed.
   */
  initials?: string;
  onClick?: () => void;
  /**
   * `false` renders a plain span instead of a button — needed wherever the avatar sits INSIDE
   * another control (the account menu's Profile row), because a button in a button is invalid
   * markup and swallows the outer row's click.
   */
  interactive?: boolean;
  /** Circle diameter in px. 26 on the bar; 18 inside a menu row, to land on that row's glyph lane. */
  size?: number;
  theme?: HeaderTheme;
  hover?: HeaderHover;
  /** Stories and visual tests only. */
  forceHover?: boolean;
  className?: string;
}

/** First letter of the first two words — "Guri Sitchinava" → "GS", "Guri" → "G". */
function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * The account trigger: a 26px circle centred in a 30px slot. There is deliberately no credit ring
 * around it — v1.5 moves the credits readout into the account menu (`CreditsMeter`), where it has
 * room for a number instead of an arc nobody can read at 26px.
 */
export function Avatar({
  src,
  name = 'User',
  initials,
  onClick,
  interactive = true,
  size = 26,
  theme: themeProp,
  hover = 'shape',
  forceHover = false,
  className,
}: AvatarProps) {
  const theme = useV15Theme(themeProp);
  const classes = cn(
    themes[theme],
    interactive ? styles.slot : styles.bare,
    hover === 'tone' && styles.tone,
    hover === 'shape' && styles.shape,
    forceHover && styles.forceHover,
    className,
  );
  const sized = { ['--hv15-avatar-size' as string]: `${size}px` } as React.CSSProperties;

  const face = (
    <span className={styles.circle}>
      {src ? <img className={styles.image} src={src} alt="" /> : (initials ?? initialsOf(name))}
    </span>
  );

  if (!interactive) {
    return (
      <span className={classes} style={sized} aria-hidden>
        {face}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={classes}
      style={sized}
      aria-label={`Account: ${name}`}
      title={name}
      // Every use of the clickable avatar opens the account menu, and saying so is what keeps the
      // header's outside-press dismissal from treating this as "somewhere else" — a press it closed
      // on would be re-opened by the click a moment later, so the avatar could never close its own
      // menu. It is also simply true, and a reader gets told what the button does.
      aria-haspopup="menu"
      onClick={onClick}
    >
      {face}
    </button>
  );
}
